import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Job } from "@/lib/schema";
import { JobInfoSection } from "./JobInfoSection";
import { ReferralForm } from "./ReferralForm";


interface ReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
}

export function ReferralModal({ open, onOpenChange, job }: ReferralModalProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  if (!job) return null;

  return (
    <>
      {/* Global Styles for Modal */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Additional scrollbar hiding for all browsers */
        .dialog-content-wrapper *::-webkit-scrollbar {
          display: none;
        }
        .dialog-content-wrapper * {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Specifically target all scrollable areas in the modal */
        .dialog-content-wrapper .overflow-y-auto,
        .dialog-content-wrapper .overflow-x-auto,
        .dialog-content-wrapper .overflow-auto {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .dialog-content-wrapper .overflow-y-auto::-webkit-scrollbar,
        .dialog-content-wrapper .overflow-x-auto::-webkit-scrollbar,
        .dialog-content-wrapper .overflow-auto::-webkit-scrollbar {
          display: none;
        }
        button[data-radix-dialog-close] {
          display: none !important;
        }
        .dialog-content-wrapper button[aria-label="Close"] {
          display: none !important;
        }
        .dialog-content-wrapper [data-radix-dialog-close] {
          display: none !important;
        }
        .dialog-content-wrapper .main-content {
          z-index: 1 !important;
          position: relative;
        }
        
        /* Prevent auto-scroll for form elements */
        .dialog-content-wrapper * {
          scroll-margin-top: 0 !important;
          scroll-margin-bottom: 0 !important;
          scroll-padding: 0 !important;
        }
        
        /* Prevent checkbox focus scrolling */
        .dialog-content-wrapper [role="checkbox"] {
          scroll-margin: 0 !important;
        }
        
        /* Override any scroll-into-view behavior */
        .dialog-content-wrapper input:focus,
        .dialog-content-wrapper button:focus,
        .dialog-content-wrapper [role="checkbox"]:focus {
          scroll-margin: 0 !important;
          scroll-behavior: auto !important;
        }
        
        /* iOS Safari specific fixes */
        .dialog-content-wrapper {
          -webkit-overflow-scrolling: touch;
          touch-action: pan-y;
        }
        
        .dialog-content-wrapper input[type="file"] {
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Ensure inputs are visible when iOS keyboard appears */
        .dialog-content-wrapper input,
        .dialog-content-wrapper textarea {
          scroll-margin-top: 100px;
          scroll-margin-bottom: 100px;
        }
        
        /* Prevent iOS keyboard from hiding inputs */
        @supports (-webkit-touch-callout: none) {
          .dialog-content-wrapper input:focus,
          .dialog-content-wrapper textarea:focus {
            position: relative;
            z-index: 1;
          }
        }
        
        /* Fix aria-hidden focus conflicts */
        [data-state="closed"][aria-hidden="true"] * {
          pointer-events: none !important;
        }
        
        [data-state="closed"][aria-hidden="true"] input,
        [data-state="closed"][aria-hidden="true"] button,
        [data-state="closed"][aria-hidden="true"] [role="checkbox"] {
          visibility: hidden !important;
        }
        
        /* Ensure Select dropdowns appear above modal - CRITICAL FIX */
        [data-radix-select-content] {
          z-index: 200 !important;
          pointer-events: auto !important;
        }
        
        [data-radix-select-viewport] {
          z-index: 200 !important;
          pointer-events: auto !important;
        }
        
        [data-radix-select-item] {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        
        /* Ensure portal container is above modal */
        [data-radix-portal] {
          z-index: 200 !important;
        }
        
        /* Prevent scroll on Select interactions */
        .dialog-content-wrapper [data-radix-select-trigger],
        .dialog-content-wrapper [data-radix-select-trigger]:focus,
        .dialog-content-wrapper [data-radix-select-trigger]:focus-visible {
          scroll-margin: 0 !important;
          scroll-padding: 0 !important;
          scroll-behavior: auto !important;
        }
        
        /* Prevent scroll into view for Select */
        .dialog-content-wrapper #relationship {
          scroll-margin-top: 0 !important;
          scroll-margin-bottom: 0 !important;
        }
      `}</style>

      <Dialog 
        open={open} 
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            handleClose();
          }
        }}
      >
        <DialogContent 
          className="dialog-content-wrapper w-[95vw] max-w-6xl h-[95vh] max-h-[95vh] p-0 soft-gradient border-0 shadow-2xl backdrop-blur-sm rounded-2xl" 
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            const tagName = target.tagName?.toLowerCase();
            const role = target.getAttribute('role');
            
            // Allow ALL Select-related interactions
            const isSelectRelated = 
              target.closest('[data-radix-select-content]') ||
              target.closest('[data-radix-select-viewport]') ||
              target.closest('[data-radix-portal]') ||
              target.hasAttribute('data-radix-select-item') ||
              role === 'option' ||
              tagName === 'select' ||
              target.classList.contains('z-[200]');
            
            // Check if file input is active (iOS Safari compatibility)
            const isFileInputActive = 
              target.closest('input[type="file"]') ||
              document.activeElement?.getAttribute('type') === 'file';
            
            if (isSelectRelated || isFileInputActive) {
              return; // Allow the click
            }
            
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            handleClose();
          }}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          role="dialog"
          aria-modal="true"
        >
          {/* Required accessibility components - visually hidden */}
          <DialogTitle asChild>
            <VisuallyHidden>
              <h2 id="modal-title">Refer a candidate for {job.title}</h2>
            </VisuallyHidden>
          </DialogTitle>
          <DialogDescription asChild>
            <VisuallyHidden>
              <p id="modal-description">
                Submit a referral by providing candidate details and explaining why they're a good fit for this position.
              </p>
            </VisuallyHidden>
          </DialogDescription>
          {/* Custom Close Button - iOS Safe Area Support */}
          <button
            onClick={handleClose}
            className="absolute right-3 z-50 w-9 h-9 bg-white/90 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-200 border border-gray-200/50 backdrop-blur-sm hover:bg-white"
            style={{ top: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
            type="button"
            aria-label="Close modal"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Enhanced Background Effects */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/20 via-transparent to-slate-50/30"></div>
            <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-orange-400/10 to-orange-600/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-gradient-to-br from-orange-400/8 to-pink-400/6 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          </div>
          
          {/* Main Content Container - SINGLE FORM INSTANCE */}
          <div className="h-full relative main-content">
            {/* Unified responsive layout - Single form instance with scroll container */}
            <div className="absolute inset-0 overflow-y-auto scrollbar-hide">
              <div className="flex flex-col lg:flex-row min-h-full">
                <JobInfoSection job={job} />
                <ReferralForm job={job} onClose={handleClose} />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
